/**
 * @typedef Point
 * @property {number} lat
 * @property {number} lng
 */

/**
 * Check's if a `point` is within a multi-polygon (`polys`).
 *
 * It relies on Jordan's Curve Theorem by determining the parity of the number
 * of intersections between an infinite ray in the direction (1, 0). The hit
 * counts are calculated by performing line-line intersection tests between
 * the ray and each face of the polygon.
 *
 * It should be noted that we simplify a large portion of the math by
 * projecting lat,lng (spherical space) in to 2d euclidean space. The
 * consequences of this is polygons must not cross the anti-meridian. Also a
 * slight warping effect will be noticed along very large distances or very
 * close to the poles. As these edge cases are unlikely, bringing down the
 * average case time complexity to support them was uneccessary.
 *
 * @param {Point} point
 * @param {Point[][]} polys
 * @return boolean
 */
function pointWithinPolys(point, polys) {
  if (!point.lat || !point.lng) {
    return false;
  }

  let hitCount = 0;

  // foreach poly in polys:
  //   foreach edge of poly:
  //     check that the point within the correct y
  //     model line as line eq L(x)
  //     solve for x such that L(x) = phi
  //     if x is within bounds increment hitCount
  for (const poly of polys) {
    for (let i = 0, e = poly.length; i < e; i++) {
      // Construct constituent vertices of the edge
      let A = poly[i];
      let B = poly[i + 1] || poly[0];

      // We first compare the y by checking the two cases:
      //  i)  Upward crossing   <== point.lat \in [A.lat, B.lat)
      //  ii) Downward crossing <== point.lat \in [B.lat, A.lat)
      const upCrossing = A.lat <= point.lat && B.lat > point.lat;
      const downCrossing = A.lat > point.lat && B.lat <= point.lat;

      // Either case must be true for a line-edge intersection
      if (!upCrossing && !downCrossing) continue;

      // compute  the actual edge-ray intersect x-coordinate
      const t = (point.lat - A.lat) / (B.lat - A.lat);
      const x = A.lng + t * (B.lng - A.lng);

      // If the intersection is to thw right of `point`, x > point.lng
      if (point.lng < x) ++hitCount;
    }
  }

  // returns true for an odd parity and false otherwise
  const oddHitCount = Boolean(hitCount & 1);
  return oddHitCount;
}

/**
 * @typedef AABB
 * @property {Point} min
 * @property {Point} max
 */

/**
 * Check's if a `point` is within a axis-aligned bounding-box (`aabb`).
 *
 * Also note: `point` and `aabb` follow the Point and AABB schemas as laid out
 * in typescript syntax below:
 *
 * @param {Point} point
 * @param {{ min: Point, max: Point }} aabb
 * @return boolean
 */
function pointWithinAABB(point, aabb) {
  return (
    point.lng > aabb.min.lng &&
    point.lat > aabb.min.lat &&
    point.lng < aabb.max.lng &&
    point.lat < aabb.max.lat
  );
}

/**
 * Check's if a `point` is within a polygon (`poly`).
 *
 * It relies on Jordan's Theorem by determining the parity of the number of
 * intersections between an infinite ray in the direction (1, 0).
 * The hit counts are calculated by performing line-line intersection tests
 * between the ray and each face of the polygon.
 *
 * It should be noted that we simplify a large portion of the math by
 * projecting lat,lng (spherical space) in to 2d euclidean space.
 * The consequences of this is polygons must not cross the anti-meridian. Also
 * a slight warping effect will be noticed along very large distances or very
 * close to the poles. As these edge cases are unlikely, bringing down the
 * average case time complexity to support them was uneccessary.
 *
 * Also note: `point` and `poly` follow the Point and Poly schemas as layed
 * out in typescript syntax below:
 *
 * @param {Point} point
 * @param {Point[]} poly
 * @return boolean
 */
function pointWithinPoly(point, poly) {
  if (!point.lat || !point.lng) {
    return false;
  }

  let hitCount = 0;

  // note: we project space such that lng -> x, lat -> y
  const theta = point.lng;
  const phi = point.lat;

  // foreach line in poly:
  //     model line as line eq L(x)
  //     solve for x such that L(x) = phi
  //     if x is within bounds increment hitCount
  for (let i = 0, e = poly.length; i < e; i++) {
    // line
    let A = poly[i];
    let B = poly[i + 1] || poly[0];

    // model line as linear eq
    // y = mx + b

    // find the slope of the line
    const delta = {
      lat: B.lat - A.lat,
      lng: B.lng - A.lng,
    };
    let m = delta.lat / delta.lng;

    // This handles the case that delta.lng === 0.
    // In this case, our line follows the relation (x = 0).
    // We can approximate the line with (y = inv_elipson * x)
    //    where inv_elipson = 1000000000
    if (m === Infinity) {
      const elipson = 0.0000000001;
      m = delta.lat / (delta.lng + elipson);
    }

    // find b:
    // y - mx = b
    const b = A.lat - m * A.lng;

    // now find x such that L(x) = theta
    // (y - b) / m = x
    let x = (phi - b) / m;

    // Now check bounds.
    // This is required as we are doing a line <-> segment intersection
    // detection, not line <-> line.
    if (
      x > theta &&
      Math.min(A.lat, B.lat) < phi &&
      phi < Math.max(A.lat, B.lat)
    ) {
      hitCount++;
    }
  }

  return !!(hitCount & 1);
}

module.exports = {
  pointWithinPoly,
  pointWithinPolys,
  pointWithinAABB,
};
